import { useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ProductBOMForm from "@/components/ProductBOMForm";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { isPackSplit, isAutoQty, isPalletLevel, isPerBottle, isAutoC, isAutoLine, isVolC, isPackSplitC, isPalletLevelC, isPalletLine, isTruckLine, basisOfLine, BASIS_RANK, dimOf, qtyInBase, fmtInUnit, filmGramsPerMeter } from "@/lib/bom";
import AddStockDialog from "@/components/AddStockDialog";
import {
  Layers,
  Plus,
  Loader2,
  MoreVertical,
  Pencil,
  PackagePlus,
  Trash2,
  ArrowLeft,
  ImageIcon,
  RotateCcw,
  Truck,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DODialog } from "@/pages/DeliveryOrders";

function Thumb({ src, className, iconSize = "h-8 w-8" }) {
  const [err, setErr] = useState(false);
  if (!src || err)
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-300 ${className}`}>
        <ImageIcon className={iconSize} strokeWidth={1.25} />
      </div>
    );
  return (
    <div className={`flex items-center justify-center bg-slate-50 ${className}`}>
      <img src={resolveImageUrl(src)} alt="" loading="lazy" decoding="async" className="max-h-full max-w-full object-contain p-2" onError={() => setErr(true)} />
    </div>
  );
}

function ProductCard({ p, onEdit, onDelete, components = [], customers = [], productsAll = [], avgCosts = {} }) {
  const [cfgIdx, setCfgIdx] = useState(0);
  const [bomView, setBomView] = useState("unit");
  const [wUnit, setWUnit] = useState("lb");
  const [addStockOpen, setAddStockOpen] = useState(false);
  const [bolPickOpen, setBolPickOpen] = useState(false);
  const [bolCustomerId, setBolCustomerId] = useState("");
  const [bolCustomer, setBolCustomer] = useState(null);
  const [configsState, setConfigsState] = useState(p.pack_configs || []);
  const [wEdit, setWEdit] = useState(null); // { field, val }
  useEffect(() => { setConfigsState(p.pack_configs || []); }, [p]);
  const [designSel, setDesignSel] = useState(() =>
    Object.fromEntries((p.lines || []).map((l) => [l.component_id, l.design_id || ""])));
  const configs = configsState;
  const multiConfig = configs.length > 1;
  const activeCfg = configs[cfgIdx] || configs[0] || {};

  const designLine = (p.lines || []).find((l) => /label/i.test(l.component_name || "") && (l.designs || []).length > 0)
    || (p.lines || []).find((l) => (l.designs || []).length > 0);
  const selDesign = designLine ? (designLine.designs || []).find((d) => d.id === designSel[designLine.component_id]) : null;
  const assocsFor = (l) => (selDesign ? (selDesign.associations || []).filter((a) => a.component_id === l.component_id && a.variant_id) : []);
  // Hide the Additives line when a selected design has no associated additive.
  const hideLine = (l) => selDesign && /additive/i.test(l.component_name || "") && assocsFor(l).length === 0;
  const compOf = (l) => (components || []).find((c) => c.id === l.component_id);

  const setDesign = async (l, designId) => {
    setDesignSel((s) => ({ ...s, [l.component_id]: designId }));
    try {
      await api.put(`/products/${p.id}/line-design`, { component_id: l.component_id, design_id: designId });
    } catch (e) {
      toast.error("Failed to save design");
    }
  };

  const lineParts = (l) => {
    if (l.missing) return { name: "(removed component)", type: "", missing: true };
    const v = variantFor(l.component_id, effectiveTypeId(l));
    const type = v ? v.t.name : (l.type_name || "—");
    return { name: l.component_name, type, missing: false };
  };

  const qtyFor = (l) => {
    const c = compOf(l);
    const cps = activeCfg.bottles_per_case || 0;
    const q = qtyInBase(l, c, activeCfg.id);
    return basisOfLine(l, c) === "per_bottle" ? q * cps : q;
  };

  const fmt = (n) => {
    if (n == null || isNaN(n)) return "—";
    return String(Math.round(n * 100) / 100);
  };
  const WEIGHT_G = { g: 1, kg: 1000, lb: 453.592, oz: 28.3495, mg: 0.001, ml: 1, "g/cm³": 1, "g/cm3": 1, "g/l": 0.001 };
  const toGrams = (u) => WEIGHT_G[(u || "g").toLowerCase()] ?? 1;
  // Effective variant for a line: pack-split variant, else the design-driven variant (cap/water/additive), else its own.
  const effectiveTypeId = (l) => {
    if (multiConfig && basisOfLine(l, compOf(l)) === "per_case") return (l.config_type_ids || {})[activeCfg.id] || l.type_id;
    const a = assocsFor(l)[0];
    if (a) return a.variant_id;
    return l.type_id;
  };
  const variantFor = (componentId, typeId) => {
    const comp = (components || []).find((c) => c.id === componentId);
    const t = comp && (comp.types || []).find((x) => x.id === typeId);
    return comp && t ? { comp, t } : null;
  };
  const gramsOf = (comp, t, rawQty) => {
    if (!comp || rawQty == null) return null;
    const d = dimOf(comp);
    if (d === "weight") return rawQty; // rawQty already in base grams
    if (d === "length" && t) {
      const gpm = filmGramsPerMeter(t);
      if (gpm != null) return rawQty * gpm; // rawQty in base metres
    }
    const wpu = t?.weight_per_unit ?? comp.default_weight_per_unit;
    if (wpu == null) return null;
    const wu = t?.weight_per_unit != null ? t.weight_unit : comp.default_weight_unit;
    return rawQty * wpu * toGrams(wu);
  };
  const lineGramsAt = (l, mult) => {
    if (mult == null) return null;
    const v = variantFor(l.component_id, effectiveTypeId(l));
    return gramsOf(v?.comp || compOf(l), v?.t || null, qtyFor(l) * mult);
  };
  const fmtWeight = (g) => (g == null ? "—" : (g / (wUnit === "kg" ? 1000 : 453.592)).toLocaleString(undefined, { maximumFractionDigits: 3 }));
  const waterLine = (p.lines || []).find((x) => dimOf(compOf(x)) === "volume");
  const waterLitresAt = (mult) => (waterLine && mult != null ? (qtyFor(waterLine) * mult) / 1000 : null);
  const casePackSize = activeCfg.bottles_per_case;
  const casesPerPallet = activeCfg.cases_per_pallet != null ? activeCfg.cases_per_pallet : null;
  const casesPerTruck = casesPerPallet != null && activeCfg.pallets_per_truckload != null ? casesPerPallet * activeCfg.pallets_per_truckload : null;
  const sections = [];
  if (casePackSize) sections.push({ key: "unit", short: "Unit", title: "Unit BOM", sub: "per bottle", caseMult: 1 / casePackSize, palletMult: null, truckMult: casesPerTruck != null && casePackSize ? 1 / (casesPerTruck * casePackSize) : null });
  sections.push({ key: "case", short: "Case", title: "Case BOM", sub: "per case", caseMult: 1, palletMult: null, truckMult: casesPerTruck != null ? 1 / casesPerTruck : null });
  if (casesPerPallet != null) sections.push({ key: "pallet", short: "Pallet", title: "Pallet BOM", sub: `${fmt(casesPerPallet)} cases / pallet`, caseMult: casesPerPallet, palletMult: 1, truckMult: activeCfg.pallets_per_truckload != null ? 1 / activeCfg.pallets_per_truckload : null });
  if (casesPerTruck != null) sections.push({ key: "truckload", short: "Truckload", title: "Truckload BOM", sub: `${fmt(casesPerTruck)} cases`, caseMult: casesPerTruck, palletMult: activeCfg.pallets_per_truckload, truckMult: 1 });
  const activeKey = sections.some((x) => x.key === bomView) ? bomView : "case";
  const activeSection = sections.find((x) => x.key === activeKey) || sections[0];
  const tabRank = { unit: 0, case: 1, pallet: 2, truckload: 3 };
  const orderedTabs = [...sections].sort((a, b) => tabRank[a.key] - tabRank[b.key]);
  const buildRows = (sec) => {
    if (!sec) return [];
    const rows = [];
    const secCases = sec.caseMult ?? null;
    const secPallets = sec.palletMult ?? null;
    const secBottles = secCases != null && casePackSize ? secCases * casePackSize : secCases;
    const recipeScale = (basis, parentMl) => {
      if (basis === "per_bottle") return secBottles;
      if (basis === "per_case") return secCases;
      if (basis === "per_pallet") return secPallets;
      if (basis === "per_litre_parent") return parentMl != null ? parentMl / 1000 : null;
      return null;
    };
    const pushRecipeRows = (l, mult, variantId) => {
      const v = variantFor(l.component_id, variantId);
      const parentMl = v && (v.comp.dimension || "count") === "volume" ? qtyFor(l) * mult : null;
      (v?.t?.recipe || []).forEach((r, ri) => {
        const scale = recipeScale(r.basis, parentMl);
        const childQty = scale == null ? null : (Number(r.quantity) || 0) * scale;
        const cv = variantFor(r.component_id, r.variant_id);
        const cname = (components || []).find((c) => c.id === r.component_id)?.name || "Ingredient";
        const rToG = { g: 1, kg: 1000, oz: 28.3495, lb: 453.592, lbs: 453.592 }[(r.unit || "").toLowerCase()];
        const grams = childQty != null && rToG ? childQty * rToG : (cv ? gramsOf(cv.comp, cv.t, childQty) : null);
        rows.push({ kind: "recipe", key: `${l.component_id}-${variantId}-r${ri}`, parentId: l.component_id, name: cname, variantName: cv?.t?.name || (r.mode === "pick_at_bom" ? "Pick at MO" : "—"), childQty, unit: r.unit, grams });
      });
    };
    [...(p.lines || [])].sort((a, b) => (BASIS_RANK[basisOfLine(a, compOf(a))] ?? 0) - (BASIS_RANK[basisOfLine(b, compOf(b))] ?? 0)).forEach((l) => {
      if (sec.key === "unit" && isPackSplitC(compOf(l))) return;
      if (hideLine(l)) return;
      const mult = isTruckLine(l, compOf(l)) ? sec.truckMult : isPalletLine(l, compOf(l)) ? sec.palletMult : sec.caseMult;
      if (mult == null) return;
      const assocs = assocsFor(l);
      if (assocs.length > 0) {
        assocs.forEach((a, ai) => {
          const v = variantFor(l.component_id, a.variant_id);
          const dose = Number(a.dose_g_per_l) > 0 ? Number(a.dose_g_per_l) : null;
          let qtyText, grams;
          if (dose) {
            const wl = waterLitresAt(mult);
            const q = wl != null ? dose * wl : null;
            qtyText = q == null ? "—" : `${fmt(q)} g`;
            grams = q;
          } else {
            qtyText = fmtInUnit(dimOf(compOf(l)), qtyFor(l) * mult, l.qty_unit);
            grams = gramsOf(compOf(l), v?.t || null, qtyFor(l) * mult);
          }
          rows.push({ kind: "line", l, mult, grams, typeName: a.variant_name || v?.t?.name || "—", qtyText, assocIdx: ai, derived: true });
          pushRecipeRows(l, mult, a.variant_id);
        });
        return;
      }
      const parts0 = lineParts(l);
      rows.push({ kind: "line", l, mult, grams: lineGramsAt(l, mult), typeName: parts0.type, qtyText: fmtInUnit(dimOf(compOf(l)), qtyFor(l) * mult, l.qty_unit), assocIdx: 0, derived: false });
      pushRecipeRows(l, mult, effectiveTypeId(l));
    });
    return rows;
  };
  const displayRows = buildRows(activeSection);
  const activeTotalGrams = displayRows.reduce((acc, row) => (row.grams == null ? acc : acc + row.grams), 0);
  const rowEstCost = (row) => {
    if (row.kind !== "line" || row.l.missing) return null;
    const ac = avgCosts[row.l.component_id]?.avg_unit_cost;
    if (!ac || dimOf(compOf(row.l)) !== "count") return null;
    const q = qtyFor(row.l) * row.mult;
    return q > 0 ? ac * q : null;
  };
  const rowCosts = displayRows.map(rowEstCost);
  const estMaterialCost = rowCosts.some((v) => v != null) ? rowCosts.reduce((a, v) => a + (v || 0), 0) : null;
  const costedCount = rowCosts.filter((v) => v != null).length;
  const lineRowCount = displayRows.filter((r) => r.kind === "line").length;
  const fmtCost = (v) => (v == null ? "—" : `$${v >= 0.1 ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v.toFixed(4)}`);
  const calcGramsAt = (key) => {
    const s = sections.find((x) => x.key === key);
    if (!s) return null;
    const rs = buildRows(s);
    if (!rs.some((r) => r.grams != null)) return null;
    return rs.reduce((acc, r) => acc + (r.grams || 0), 0);
  };
  const calcBottleLbs = (() => { const g = calcGramsAt("unit"); return g == null ? null : g / 453.592; })();
  const calcCaseLbs = (() => { const g = calcGramsAt("case"); return g == null ? null : g / 453.592; })();
  const calcPalletLbs = (() => { const g = calcGramsAt("pallet"); return g == null ? null : g / 453.592; })();
  const saveWeight = async (field, value) => {
    try {
      const { data } = await api.put(`/products/${p.id}/pack-weight`, { config_id: activeCfg.id, field, value });
      setConfigsState(data.pack_configs || []);
      setWEdit(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save weight");
    }
  };

  return (
    <div className="group flex flex-col overflow-hidden border border-slate-200 bg-white text-left transition-colors hover:border-[#002FA7] md:flex-row" data-testid={`product-card-${p.id}`}>
      <div className="flex shrink-0 flex-col border-b border-slate-200 md:w-72 md:border-b-0 md:border-r">
        <button onClick={() => onEdit(p.id)} className="block w-full text-left" data-testid={`open-product-${p.id}`}>
          <Thumb key={selDesign?.artwork_image_url || p.image_url} src={selDesign?.artwork_image_url || p.image_url} className="aspect-[4/3] w-full border-b border-slate-200" iconSize="h-10 w-10" />
        </button>
        <div className="flex items-start justify-between gap-2 p-4">
          <button onClick={() => onEdit(p.id)} className="min-w-0 text-left">
            <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 truncate">{p.name}</h3>
            <p className="mt-1 text-xs uppercase tracking-[0.12em] text-slate-400">{p.lines?.length || 0} component{(p.lines?.length || 0) !== 1 ? "s" : ""}</p>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-slate-500" data-testid={`product-menu-${p.id}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => onEdit(p.id)} data-testid={`edit-product-${p.id}`}>
                <Pencil className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => onDelete(p)} data-testid={`delete-product-${p.id}`}>
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="px-4 pb-2">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setAddStockOpen(true)}
              className="h-8 flex-1 rounded-sm border-[#002FA7]/40 text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7]/5"
              data-testid={`add-fg-stock-btn-${p.id}`}>
              <PackagePlus className="mr-1.5 h-3.5 w-3.5" /> Add Stock
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setBolCustomerId(""); setBolPickOpen(true); }}
              className="h-8 flex-1 rounded-sm border-slate-300 text-xs font-semibold text-slate-600 hover:border-[#002FA7]/40 hover:text-[#002FA7]"
              data-testid={`create-bol-btn-${p.id}`}>
              <Truck className="mr-1.5 h-3.5 w-3.5" /> Create BOL
            </Button>
          </div>
          <div className="mt-2 space-y-1.5 text-[11px] text-slate-500" data-testid={`pack-facts-${p.id}`}>
            {(() => {
              const num = (v) => (v != null && v !== "" ? Number(v) : null);
              const fmtV = (v, suffix = "") => (num(v) != null ? `${Number(v).toLocaleString()}${suffix}` : "—");
              const bpc = num(activeCfg.bottles_per_case);
              const cpp = num(activeCfg.cases_per_pallet);
              const bppCalc = bpc != null && cpp != null ? bpc * cpp : null;
              const dims = [activeCfg.pallet_length, activeCfg.pallet_width, activeCfg.pallet_height];
              const hasDims = dims.some((d) => num(d) != null);
              const dimStr = hasDims ? dims.map((d) => (num(d) != null ? Number(d).toLocaleString() : "?")).join(" × ") + " in" : "—";
              const cDims = [activeCfg.case_length, activeCfg.case_width, activeCfg.case_height];
              const hasCDims = cDims.some((d) => num(d) != null);
              const cDimStr = hasCDims ? cDims.map((d) => (num(d) != null ? Number(d).toLocaleString() : "?")).join(" × ") + " in" : "—";
              const Row = ({ l, v }) => (
                <div className="flex justify-between"><span>{l}</span><span className="font-semibold text-slate-700">{v}</span></div>
              );
              const fmtLbs = (n) => `${Number(n).toLocaleString(undefined, { maximumFractionDigits: Math.abs(Number(n)) < 1 ? 3 : 2 })} lbs`;
              const WeightRow = ({ label, field, calcLbs }) => {
                const manual = num(activeCfg[field]);
                if (wEdit?.field === field) {
                  const commit = () => saveWeight(field, wEdit.val === "" ? null : Number(wEdit.val));
                  return (
                    <div className="flex items-center justify-between gap-2">
                      <span>{label}</span>
                      <input autoFocus value={wEdit.val}
                        onChange={(e) => setWEdit({ field, val: e.target.value.replace(/[^0-9.]/g, "") })}
                        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setWEdit(null); }}
                        onBlur={commit}
                        className="w-20 rounded-sm border border-slate-300 px-1.5 py-0.5 text-right text-[11px] font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                        data-testid={`weight-input-${field}-${p.id}`} placeholder="lbs" />
                    </div>
                  );
                }
                if (manual != null) {
                  return (
                    <div className="flex items-center justify-between gap-1">
                      <span>{label}</span>
                      <span className="flex items-center gap-1">
                        <span className="rounded-sm bg-amber-50 px-1 text-[9px] font-bold uppercase tracking-wide text-amber-600">manual</span>
                        <button type="button" onClick={() => setWEdit({ field, val: String(manual) })} className="font-semibold text-slate-700 underline decoration-dotted underline-offset-2" title="Edit manual weight" data-testid={`weight-value-${field}-${p.id}`}>{fmtLbs(manual)}</button>
                        <button type="button" onClick={() => saveWeight(field, null)} title={`Revert to calculated${calcLbs != null ? ` (${fmtLbs(calcLbs)})` : ""}`} className="text-slate-400 transition-colors hover:text-[#002FA7]" data-testid={`weight-revert-${field}-${p.id}`}>
                          <RotateCcw className="h-3 w-3" />
                        </button>
                      </span>
                    </div>
                  );
                }
                return (
                  <div className="flex items-center justify-between gap-1">
                    <span>{label}</span>
                    <span className="flex items-center gap-1">
                      {calcLbs != null && <span className="rounded-sm bg-slate-100 px-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">calc</span>}
                      <span className="font-semibold text-slate-700" data-testid={`weight-value-${field}-${p.id}`}>{calcLbs != null ? fmtLbs(calcLbs) : "—"}</span>
                      <button type="button" onClick={() => setWEdit({ field, val: "" })} title="Override with a manual weight" className="text-slate-400 transition-colors hover:text-[#002FA7]" data-testid={`weight-edit-${field}-${p.id}`}>
                        <Pencil className="h-3 w-3" />
                      </button>
                    </span>
                  </div>
                );
              };
              return (
                <>
                  <p className="pt-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#002FA7]">Case Information</p>
                  <WeightRow label="Bottle Weight" field="weight_per_bottle" calcLbs={calcBottleLbs} />
                  <Row l="Bottles per Case" v={fmtV(activeCfg.bottles_per_case)} />
                  <WeightRow label="Case Weight" field="weight_per_case" calcLbs={calcCaseLbs} />
                  <Row l="Case Dimensions (L×W×H)" v={cDimStr} />
                  <p className="pt-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#002FA7]">Pallet Information</p>
                  <Row l="Cases per Pallet" v={fmtV(activeCfg.cases_per_pallet)} />
                  <Row l="Bottles per Pallet" v={bppCalc != null ? bppCalc.toLocaleString() : "—"} />
                  <WeightRow label="Pallet Weight" field="weight_per_pallet" calcLbs={calcPalletLbs} />
                  <Row l="Pallet Dimensions (L×W×H)" v={dimStr} />
                  <p className="pt-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#002FA7]">Truckload Information</p>
                  {(() => {
                    const ppt = num(activeCfg.pallets_per_truckload);
                    const cptl = ppt != null && cpp != null ? ppt * cpp : null;
                    const bptl = cptl != null && bpc != null ? cptl * bpc : null;
                    const palletLbs = num(activeCfg.weight_per_pallet) != null ? num(activeCfg.weight_per_pallet) : calcPalletLbs;
                    const tlLbs = ppt != null && palletLbs != null ? ppt * palletLbs : null;
                    return (
                      <>
                        <Row l="Pallets per Truckload" v={fmtV(activeCfg.pallets_per_truckload)} />
                        <Row l="Cases per Truckload" v={cptl != null ? cptl.toLocaleString() : "—"} />
                        <Row l="Bottles per Truckload" v={bptl != null ? bptl.toLocaleString() : "—"} />
                        <Row l="Truckload Weight" v={tlLbs != null ? fmtLbs(tlLbs) : "—"} />
                      </>
                    );
                  })()}
                </>
              );
            })()}
          </div>
        </div>
        {multiConfig ? (
          <div className="mt-auto flex flex-wrap gap-1 px-4 pb-4" data-testid={`pack-toggle-${p.id}`}>
            {configs.map((cfg, ci) => (
              <button
                key={cfg.id || ci}
                onClick={() => setCfgIdx(ci)}
                className={`flex-1 rounded-sm border px-2 py-1 text-xs font-semibold uppercase tracking-[0.05em] transition-colors ${cfgIdx === ci ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                data-testid={`pack-view-${ci}-${p.id}`}
              >
                {cfg.label || `${cfg.bottles_per_case || "?"} Pack`}
              </button>
            ))}
          </div>
        ) : activeCfg.bottles_per_case != null ? (
          <div className="mt-auto flex px-4 pb-4" data-testid={`case-pack-badge-${p.id}`}>
            <div className="flex-1 rounded-sm border border-[#002FA7] bg-[#002FA7] px-2 py-1 text-center text-xs font-semibold uppercase tracking-[0.05em] text-white">
              {activeCfg.label || `${activeCfg.bottles_per_case} Pack`}
            </div>
          </div>
        ) : null}
      </div>
      {p.lines?.length > 0 ? (
        <div className="min-w-0 flex-1 p-4" data-testid={`bom-breakdown-${p.id}`}>
          <div className="flex flex-wrap gap-2">
            {orderedTabs.map((s) => (
              <button
                key={s.key}
                onClick={() => setBomView(s.key)}
                className={`rounded-sm border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.05em] transition-colors ${activeKey === s.key ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                data-testid={`bom-tab-${s.key}-${p.id}`}
              >
                {s.short}
              </button>
            ))}
          </div>
          <div className="mt-3" data-testid={`bom-section-${activeSection.key}-${p.id}`}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#002FA7]">{activeSection.title}</p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.06em] text-slate-400">{activeSection.sub}</span>
                <div className="flex overflow-hidden rounded-sm border border-slate-200">
                  {["kg", "lb"].map((u) => (
                    <button key={u} type="button" onClick={() => setWUnit(u)} className={`px-2 py-0.5 text-[10px] font-semibold uppercase transition-colors ${wUnit === u ? "bg-[#002FA7] text-white" : "text-slate-500 hover:bg-slate-50"}`} data-testid={`weight-unit-${u}-${p.id}`}>{u}</button>
                  ))}
                </div>
              </div>
            </div>
            <table className="mt-2 w-full border-collapse text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.08em] text-slate-500">
                  <th className="border border-slate-300 bg-slate-100 px-3 py-1.5 text-left font-semibold">Component</th>
                  <th className="border border-slate-300 bg-slate-100 px-3 py-1.5 text-left font-semibold">Type</th>
                  <th className="w-28 border border-slate-300 bg-slate-100 px-3 py-1.5 text-right font-semibold">Qty</th>
                  <th className="w-24 border border-slate-300 bg-slate-100 px-3 py-1.5 text-right font-semibold">Est. Cost</th>
                  <th className="w-28 border border-slate-300 bg-slate-100 px-3 py-1.5 text-right font-semibold">Weight ({wUnit})</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, idx) => {
                  if (row.kind === "recipe") {
                    const toG = { g: 1, kg: 1000, oz: 28.3495, lb: 453.592, lbs: 453.592 }[(row.unit || "").toLowerCase()];
                    let qtyDisplay;
                    if (row.childQty == null) qtyDisplay = "—";
                    else if (toG) {
                      const g = row.childQty * toG;
                      qtyDisplay = g < 453.592 ? `${fmt(Math.round(g * 100) / 100)} g` : `${fmt(Math.round((g / 453.592) * 100) / 100)} lb`;
                    } else qtyDisplay = `${fmt(row.childQty)}${row.unit ? " " + row.unit : ""}`;
                    return (
                      <tr key={row.key} className="align-top bg-slate-50/70" data-testid={`bom-recipe-${row.parentId}-${idx}`}>
                        <td className="border border-slate-300 px-3 py-1 pl-6 text-xs italic text-slate-500">↳ {row.name}</td>
                        <td className="border border-slate-300 px-3 py-1 text-xs italic text-slate-500">{row.variantName}</td>
                        <td className="border border-slate-300 px-3 py-1 text-right text-xs text-slate-500 whitespace-nowrap">{qtyDisplay}</td>
                        <td className="border border-slate-300 px-3 py-1 text-right text-xs text-slate-400">—</td>
                        <td className="border border-slate-300 px-3 py-1 text-right text-xs text-slate-500 whitespace-nowrap">{fmtWeight(row.grams)}</td>
                      </tr>
                    );
                  }
                  const l = row.l;
                  const parts = lineParts(l);
                  return (
                    <tr key={`${l.component_id}-${idx}`} className="align-top">
                      <td className={`border border-slate-300 px-3 py-1.5 font-semibold ${parts.missing ? "text-amber-600" : "text-slate-800"}`}>{parts.name}</td>
                      <td className="border border-slate-300 px-3 py-1.5 text-slate-500">
                        <div className={row.derived ? "text-slate-800" : ""} data-testid={`bom-type-${l.component_id}`}>{row.typeName || "—"}</div>
                        {row.assocIdx === 0 && (
                          l.designs && l.designs.length > 0 ? (
                            <Select value={designSel[l.component_id] || "none"} onValueChange={(v) => setDesign(l, v === "none" ? "" : v)}>
                              <SelectTrigger className="mt-1 h-auto w-auto justify-start gap-1 rounded-sm border border-[#002FA7] bg-[#002FA7] px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-[#0041e8] focus:ring-0 focus:ring-offset-0 [&>svg]:text-white [&>svg]:opacity-90" data-testid={`design-select-${p.id}-${l.component_id}`}>
                                <span>{l.designs.find((d) => d.id === designSel[l.component_id])?.name || "No design"}</span>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— No design —</SelectItem>
                                {l.designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          ) : /label/i.test(l.component_name || "") ? (
                            <div className="mt-1 text-[10px] italic text-slate-400">Add designs on Components</div>
                          ) : null
                        )}
                      </td>
                      <td className="border border-slate-300 px-3 py-1.5 text-right font-medium text-slate-600 whitespace-nowrap">{row.qtyText}</td>
                      <td className="border border-slate-300 px-3 py-1.5 text-right whitespace-nowrap tabular-nums" data-testid={`bom-avgcost-${l.component_id}`}>
                        {rowCosts[idx] != null
                          ? <span className="font-medium text-[#002FA7]" title={`${row.qtyText} × $${avgCosts[l.component_id].avg_unit_cost.toFixed(4)}/unit (lifetime average across ${avgCosts[l.component_id].purchases} invoiced purchase${avgCosts[l.component_id].purchases !== 1 ? "s" : ""})`}>{fmtCost(rowCosts[idx])}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="border border-slate-300 px-3 py-1.5 text-right font-medium text-slate-600 whitespace-nowrap" data-testid={`bom-weight-${l.component_id}`}>{fmtWeight(row.grams)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {estMaterialCost != null && (
                  <tr className="text-xs">
                    <td className="border border-slate-300 bg-slate-50 px-3 py-1.5 text-right font-semibold uppercase tracking-[0.06em] text-slate-500" colSpan={3}>Est. material cost</td>
                    <td className="border border-slate-300 bg-slate-50 px-3 py-1.5 text-right font-bold text-[#002FA7] whitespace-nowrap tabular-nums" data-testid={`bom-est-cost-${p.id}`}
                      title={`${costedCount} of ${lineRowCount} components costed from supplier invoice averages${costedCount < lineRowCount ? " — components without invoice history aren't included" : ""}`}>
                      {fmtCost(estMaterialCost)}{costedCount < lineRowCount ? <span className="ml-1 font-semibold text-slate-400">({costedCount}/{lineRowCount})</span> : null}
                    </td>
                    <td className="border border-slate-300 bg-slate-50" />
                  </tr>
                )}
                <tr className="text-xs">
                  <td className="border border-slate-300 bg-slate-50 px-3 py-1.5 text-right font-semibold uppercase tracking-[0.06em] text-slate-500" colSpan={4}>Total weight</td>
                  <td className="border border-slate-300 bg-slate-50 px-3 py-1.5 text-right font-bold text-slate-800 whitespace-nowrap" data-testid={`bom-total-weight-${p.id}`}>{fmtWeight(activeTotalGrams)} {wUnit}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-slate-400" data-testid={`bom-empty-${p.id}`}>No components in this BOM.</div>
      )}
      <AddStockDialog open={addStockOpen} onOpenChange={setAddStockOpen} product={p} components={components}
        initialCfgId={activeCfg?.id || ""} initialDesignId={selDesign?.id || ""} />
      <Dialog open={bolPickOpen} onOpenChange={setBolPickOpen}>
        <DialogContent className="rounded-sm sm:max-w-sm" data-testid={`bol-pick-dialog-${p.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Create BOL — {p.name}</DialogTitle>
            <DialogDescription>Pick the customer this shipment goes to. The shipment starts with {p.name}{selDesign ? ` (${selDesign.name})` : ""} — you can adjust cases and add products next.</DialogDescription>
          </DialogHeader>
          <Select value={bolCustomerId || undefined} onValueChange={setBolCustomerId}>
            <SelectTrigger className="rounded-sm" data-testid={`bol-customer-select-${p.id}`}><SelectValue placeholder="Select customer…" /></SelectTrigger>
            <SelectContent>{customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}</SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" className="rounded-sm" onClick={() => setBolPickOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={!bolCustomerId}
              onClick={() => { setBolCustomer(customers.find((c) => c.id === bolCustomerId) || null); setBolPickOpen(false); }}
              data-testid={`bol-pick-continue-${p.id}`}>Continue</Button>
          </div>
        </DialogContent>
      </Dialog>
      {bolCustomer && (
        <DODialog open={!!bolCustomer} onOpenChange={(o) => { if (!o) setBolCustomer(null); }} so={null} customer={bolCustomer}
          existing={null} products={productsAll.length ? productsAll : [p]} customers={customers}
          initialLines={[(() => {
            const cfg = activeCfg || {};
            const dims = [cfg.pallet_length, cfg.pallet_width, cfg.pallet_height];
            const pallet_dims = dims.some((v) => Number(v) > 0) ? dims.map((v) => (Number(v) > 0 ? Number(v).toLocaleString() : "?")).join(" × ") + " in" : "";
            return {
              so_line_id: "", product_id: p.id, product_name: p.name,
              design_id: selDesign?.id || "", design_name: selDesign?.name || "",
              config_id: cfg.id || "", case_pack: Number(cfg.bottles_per_case) || 0,
              cases: "0", ordered: null,
              cases_per_pallet: Number(cfg.cases_per_pallet) || null,
              pallets_per_truckload: Number(cfg.pallets_per_truckload) || null,
              weight_per_case: Number(cfg.weight_per_case) || null,
              weight_per_pallet: Number(cfg.weight_per_pallet) || null,
              pallet_dims,
            };
          })()]}
          onSaved={() => setBolCustomer(null)} />
      )}
    </div>
  );
}

export default function ProductBOMs() {
  const [products, setProducts] = useState([]);
  const [catFilter, setCatFilter] = useState("ALL");
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState({ mode: "list" }); // list | form
  const [toDelete, setToDelete] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [avgCosts, setAvgCosts] = useState({});
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/avg-costs").then((r) => setAvgCosts(r.data?.by_component || {})).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get("/products"), api.get("/components")]);
      setProducts(p.data);
      setComponents(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (view.product) {
        await api.put(`/products/${view.product.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product saved");
      }
      setView({ mode: "list" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/products/${id}`);
      setView({ mode: "form", product: data });
    } catch (e) {
      toast.error("Failed to open product");
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/products/${toDelete.id}`);
      toast.success("Product deleted");
      await load();
    } catch (e) {
      toast.error("Failed to delete");
    } finally {
      setToDelete(null);
    }
  };

  if (view.mode === "form") {
    return (
      <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 md:px-10 md:py-12" data-testid="product-boms-page">
        <button onClick={() => setView({ mode: "list" })} className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-boms">
          <ArrowLeft className="h-4 w-4" /> All products
        </button>
        <h1 className="mb-8 font-heading text-2xl font-black tracking-tighter text-slate-900 sm:text-3xl">
          {view.product ? "Edit Product BOM" : "New Product BOM"}
        </h1>
        <ProductBOMForm initial={view.product || null} components={components} saving={saving} onSave={save} onCancel={() => setView({ mode: "list" })} />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 md:px-10 md:py-12" data-testid="product-boms-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Finished Goods</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">Product BOMs</h1>
          <p className="mt-2 text-sm text-slate-500">{catFilter === "ALL" ? `${products.length} products` : `${products.filter((p) => (p.category || "") === catFilter).length} of ${products.length} products`}</p>
        </div>
        <Button onClick={() => setView({ mode: "form", product: null })} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="new-bom-button">
          <Plus className="mr-1 h-4 w-4" /> New Product
        </Button>
      </div>

      <div className="mt-6 inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="category-filter">
        {["ALL", "PET", "ALUM"].map((c) => (
          <button key={c} type="button" onClick={() => setCatFilter(c)}
            className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${catFilter === c ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
            data-testid={`category-filter-${c.toLowerCase()}`}>
            {c === "ALL" ? "All" : c}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#002FA7]" /></div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-24 text-center" data-testid="empty-state">
            <Layers className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
            <h2 className="mt-4 font-heading text-xl font-bold tracking-tight text-slate-900">No products yet</h2>
            <p className="mt-2 max-w-sm text-sm text-slate-500">Create a product and list the components (bill of materials) that make it.</p>
            <Button onClick={() => setView({ mode: "form", product: null })} className="mt-6 rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="empty-new-bom-button">
              <Plus className="mr-1 h-4 w-4" /> New Product
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {products.filter((p) => catFilter === "ALL" || (p.category || "") === catFilter).map((p) => (
              <ProductCard key={p.id} p={p} components={components} customers={customers} productsAll={products} avgCosts={avgCosts} onEdit={openEdit} onDelete={setToDelete} />
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Delete "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes this product BOM.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
